import React, { useEffect, useState, useMemo, useContext } from "react";
import { createUser, checkNumistrailIdAvailable } from "../api/users";
import { listCompanies } from "../api/companies";
import { AuthContext } from "../contexts/AuthContext";
import {
  validateNumistrailId,
  validatePassword,
  validateEmail,
  validatePhone,
  normalizeInput,
} from "../utils/validation";
import { useTranslation } from "react-i18next";

export function AdminUsers() {
  const { company, isPlatformAdmin, canManageCompanyUsers } = useContext(AuthContext);
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    numistrail_id: "",
    password: "",
    email: "",
    phone: "",
    company_id: "",
    company_role: "",
  });
  const [status, setStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [companyError, setCompanyError] = useState(null);
  const [touched, setTouched] = useState({});
  const [suggestion, setSuggestion] = useState(null);
  const canSelectCompany = isPlatformAdmin;

  const companyRoleOptions = useMemo(
    () => ([
      { value: "", label: t("adminUsers.rolePlaceholder") },
      ...["expert", "admin", "reports"].map(
        (role) => ({ value: role, label: t(`adminUsers.roles.${role}`, { defaultValue: role }) })
      ),
    ]),
    [t]
  );

  useEffect(() => {
    if (canManageCompanyUsers) {
      listCompanies()
        .then((resp) => {
          const allCompanies = resp.data.companies || [];
          const eligibleCompanies = allCompanies.filter(
            (c) => c.company_type !== "MARKETPLACE"
          );
          setCompanies(eligibleCompanies);
        })
        .catch(() => setCompanyError(t("adminUsers.loadCompaniesError")));
    }
    return () => {
      setCompanies([]);
    };
  }, [canManageCompanyUsers, t]);

  useEffect(() => {
    if (!canSelectCompany && company) {
      setForm((current) => ({
        ...current,
        company_id: company.company_id,
      }));
    }
  }, [canSelectCompany, company]);

  const validationErrors = useMemo(() => {
    const companyIdError = !form.company_id && form.company_role
      ? "validation.companyId.requiredWithRole"
      : null;
    const companyRoleError = form.company_id && !form.company_role
      ? "validation.companyRole.requiredWithCompany"
      : null;

    return {
      numistrail_id: validateNumistrailId(form.numistrail_id),
      password: validatePassword(form.password),
      email: validateEmail(form.email),
      phone: validatePhone(form.phone),
      company_id: companyIdError,
      company_role: companyRoleError,
    };
  }, [form.numistrail_id, form.password, form.email, form.phone, form.company_id, form.company_role]);

  const isFormValid = useMemo(
    () => Object.values(validationErrors).every((error) => !error),
    [validationErrors]
  );

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    const nextValue = field === "email" ? value.toLowerCase() : value;
    setForm((current) => ({ ...current, [field]: nextValue }));
    setTouched((prev) => (prev[field] ? prev : { ...prev, [field]: true }));
    if (field === "numistrail_id") {
      setSuggestion(null);
    }
  };

  const markTouched = (field) => () =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const applySuggestion = () => {
    if (!suggestion) return;
    setForm((current) => ({ ...current, numistrail_id: suggestion }));
    setSuggestion(null);
    setTouched((prev) => ({ ...prev, numistrail_id: true }));
    setStatus(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isFormValid) {
      setTouched({
        numistrail_id: true,
        password: true,
        email: true,
        phone: true,
        company_id: true,
        company_role: true,
      });
      setStatus({ type: "error", text: t("validation.fixErrors") });
      return;
    }

    setStatus(null);
    setIsSubmitting(true);

    try {
      const normalizedNumisId = normalizeInput(form.numistrail_id, { lowercase: true });
      const normalizedEmail = normalizeInput(form.email, { lowercase: true });
      const normalizedPhone = normalizeInput(form.phone);

      await createUser({
        numistrail_id: normalizedNumisId,
        password: normalizeInput(form.password),
        email: normalizedEmail,
        phone: normalizedPhone || undefined,
        company_id: form.company_id || undefined,
        company_role: form.company_role || undefined,
      });
      setForm({
        numistrail_id: "",
        password: "",
        email: "",
        phone: "",
        company_id: canSelectCompany ? "" : (company?.company_id || ""),
        company_role: "",
      });
      setTouched({});
      setSuggestion(null);
      setShowPassword(false);
      setStatus({ type: "success", text: t("adminUsers.createSuccess") });
    } catch (error) {
      const rawMessage = error.response?.data?.message;
      const directMessageKey = error.response?.data?.message_key;
      const inferredMessageKey = directMessageKey || extractMessageKey(rawMessage);
      const normalizedKey = inferredMessageKey || "";
      const normalizedMessage = (rawMessage || "").toLowerCase();
      const isNumisDuplicate =
        normalizedKey === "errors.users.numis_duplicate" ||
        normalizedMessage.includes("numistrail");
      const isEmailDuplicate =
        normalizedKey === "errors.users.email_duplicate" ||
        normalizedMessage.includes("email");
      const isGenericDuplicate =
        normalizedKey === "errors.users.duplicate" ||
        normalizedMessage.includes("duplicate value");

      let statusText;
      if (isNumisDuplicate) {
        statusText = t("adminUsers.duplicateId", {
          defaultValue: "That NumisTrail ID is already taken. Try another or use the suggestion above.",
        });
      } else if (isEmailDuplicate) {
        statusText = t("adminUsers.duplicateEmail", {
          defaultValue: "That email address is already associated with another account. Please use a different email.",
        });
      } else if (isGenericDuplicate) {
        statusText = t("adminUsers.duplicateGeneric", {
          defaultValue: "A unique value is already in use. Please double-check the form and try again.",
        });
      } else if (inferredMessageKey) {
        statusText = t(inferredMessageKey, {
          defaultValue: extractMessageText(rawMessage) || t("adminUsers.createError"),
        });
      } else if (typeof rawMessage === "string" && rawMessage.trim().length) {
        statusText = extractMessageText(rawMessage);
      } else {
        statusText = t("adminUsers.createError");
      }

      setStatus({
        type: "error",
        text: statusText,
      });

      if (isNumisDuplicate) {
        const suggested = await suggestUsername(form.numistrail_id);
        if (suggested) {
          setSuggestion(suggested);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="admin-users-screen">
      <div className="admin-users-card">
        <div className="admin-users-card__header">
          <h2 className="admin-users-card__title">{t("adminUsers.title")}</h2>
          <p className="admin-users-card__subtitle">
            {t("adminUsers.subtitle")}
          </p>
        </div>

        {!canManageCompanyUsers && (
          <p className="admin-users-status admin-users-status--error">
            {t("adminUsers.notAuthorized")}
          </p>
        )}

        {canManageCompanyUsers && (
        <form className="admin-users-form" onSubmit={handleSubmit}>
          <div className="admin-users-field">
            <label className="admin-users-label" htmlFor="numistrail-id">
              {t("adminUsers.numisId")}
            </label>
            <input
              id="numistrail-id"
              type="text"
              placeholder="numistrail_id"
              value={form.numistrail_id}
              onChange={handleChange("numistrail_id")}
              onBlur={markTouched("numistrail_id")}
            />
            {touched.numistrail_id && validationErrors.numistrail_id ? (
              <p className="admin-users-field__error" role="alert">{t(validationErrors.numistrail_id)}</p>
            ) : null}
            {suggestion && (
              <div className="admin-users-suggestion">
                <span>{t("register.suggestedId")}</span>
                <button type="button" onClick={applySuggestion} className="admin-users-suggestion__button">
                  {suggestion}
                </button>
              </div>
            )}
          </div>

          <div className="admin-users-field">
            <label className="admin-users-label" htmlFor="phone">
              {t("adminUsers.phone")}
            </label>
            <input
              id="phone"
              type="text"
              placeholder="+15555555555"
              value={form.phone}
              onChange={handleChange("phone")}
              onBlur={markTouched("phone")}
            />
            {touched.phone && validationErrors.phone ? (
              <p className="admin-users-field__error" role="alert">{t(validationErrors.phone)}</p>
            ) : null}
          </div>

          <fieldset className="admin-users-fieldset">
            <legend className="admin-users-legend">{t("adminUsers.companyAssignment")}</legend>
            {!canSelectCompany && company && (
              <p className="admin-users-hint">
                {t("adminUsers.companyFixed", { company: company.display_name })}
              </p>
            )}
            {companyError && <p className="admin-users-status admin-users-status--error">{companyError}</p>}
            <div className="admin-users-field">
              <label className="admin-users-label" htmlFor="company">
                {t("adminUsers.company")}
              </label>
              <select
                id="company"
                value={form.company_id}
                onChange={handleChange("company_id")}
                disabled={!canSelectCompany && Boolean(company)}
                onBlur={markTouched("company_id")}
              >
                <option value="">{t("adminUsers.companyPlaceholder")}</option>
                {companies.map((c) => (
                  <option key={c.company_id} value={c.company_id}>
                    {c.display_name}
                  </option>
                ))}
              </select>
              {touched.company_id && validationErrors.company_id ? (
                <p className="admin-users-field__error" role="alert">{t(validationErrors.company_id)}</p>
              ) : null}
            </div>

            <div className="admin-users-field">
              <label className="admin-users-label" htmlFor="company-role">
                {t("adminUsers.role")}
              </label>
              <p className="admin-users-hint">{t("adminUsers.roleHint")}</p>
              <select
                id="company-role"
                value={form.company_role}
                onChange={handleChange("company_role")}
                onBlur={markTouched("company_role")}
              >
                {companyRoleOptions.map((option) => (
                  <option
                    key={option.value || "company-role-placeholder"}
                    value={option.value}
                    disabled={option.disabled}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
              {touched.company_role && validationErrors.company_role ? (
                <p className="admin-users-field__error" role="alert">{t(validationErrors.company_role)}</p>
              ) : null}
            </div>
          </fieldset>

          <div className="admin-users-field">
            <label className="admin-users-label" htmlFor="password">
              {t("adminUsers.password")}
            </label>
            <div className="login-password-input">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="password"
                value={form.password}
                onChange={handleChange("password")}
                onBlur={markTouched("password")}
              />
              <button
                type="button"
                className="login-toggle"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? t("login.hidePassword") : t("login.showPassword")}
              >
                {showPassword ? "🙈" : "👁️"}
              </button>
            </div>
            {touched.password && validationErrors.password ? (
              <p className="admin-users-field__error" role="alert">{t(validationErrors.password)}</p>
            ) : null}
          </div>

          <div className="admin-users-field">
            <label className="admin-users-label" htmlFor="email">
              {t("adminUsers.email")}
            </label>
            <input
              id="email"
              type="email"
              placeholder="email@example.com"
              value={form.email}
              onChange={handleChange("email")}
              onBlur={markTouched("email")}
            />
            {touched.email && validationErrors.email ? (
              <p className="admin-users-field__error" role="alert">{t(validationErrors.email)}</p>
            ) : null}
          </div>

          {status && (
            <p
              className={`admin-users-status admin-users-status--${status.type}`}
              role="status"
            >
              {status.text}
            </p>
          )}

          <button
            className="admin-users-submit"
            type="submit"
            disabled={!isFormValid || isSubmitting}
          >
            {isSubmitting ? t("adminUsers.creating") : t("adminUsers.create")}
          </button>
        </form>
        )}
      </div>
    </div>
  );
}

function extractMessageKey(rawMessage) {
  if (typeof rawMessage !== "string") return null;
  const match = rawMessage.match(/message_key['"]?\s*[:=]\s*['"]([^'"]+)['"]/);
  return match ? match[1] : null;
}

function extractMessageText(rawMessage) {
  if (typeof rawMessage !== "string") return null;
  const match = rawMessage.match(/['"]message['"]?\s*[:=]\s*['"]([^'"]+)['"]/);
  return match ? match[1] : rawMessage;
}

async function suggestUsername(base) {
  const normalizedBase = normalizeInput(base || "user", { lowercase: true }).replace(/[^a-z0-9_]/g, "").slice(0, 40) || "user";
  let attempt = 0;
  while (attempt < 5) {
    const candidate = `${normalizedBase}${Math.floor(Math.random() * 1000)}`;
    try {
      const response = await checkNumistrailIdAvailable(candidate);
      if (response.data?.available) {
        return candidate;
      }
    } catch {
      // ignore network/availability errors and try another candidate
    }
    attempt += 1;
  }
  return null;
}
